/**
 * Application Logger
 *
 * Centralized logging utility that sends logs to Supabase for debugging and monitoring.
 * Automatically batches logs and handles errors gracefully.
 */

import { supabase } from '../lib/supabase';

// Log queue for batching
let logQueue = [];
let batchTimeout = null;
const BATCH_SIZE = 20;
const BATCH_DELAY = 10000; // 10 seconds

// Session ID for tracking user sessions
let sessionId = null;

// Initialize session ID
function getSessionId() {
  if (sessionId) return sessionId;

  // Try to get existing session ID from sessionStorage
  sessionId = sessionStorage.getItem('app_session_id');

  if (!sessionId) {
    // Generate new session ID
    sessionId = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    sessionStorage.setItem('app_session_id', sessionId);
  }

  return sessionId;
}

/**
 * Send logs to database
 */
async function sendLogs(logs) {
  if (logs.length === 0) return;

  try {
    const { error } = await supabase
      .from('application_logs')
      .insert(logs);

    if (error) {
      console.error('[Logger] Failed to send logs:', error);
    }
  } catch (err) {
    console.error('[Logger] Error sending logs:', err);
  }
}

/**
 * Add log to queue and batch send
 */
function queueLog(logData) {
  logQueue.push(logData);

  // Send immediately if batch size reached
  if (logQueue.length >= BATCH_SIZE) {
    flushLogs();
  } else {
    // Schedule batch send
    if (batchTimeout) {
      clearTimeout(batchTimeout);
    }
    batchTimeout = setTimeout(flushLogs, BATCH_DELAY);
  }
}

/**
 * Flush all queued logs immediately
 */
export function flushLogs() {
  if (logQueue.length === 0) return;

  const logsToSend = [...logQueue];
  logQueue = [];

  if (batchTimeout) {
    clearTimeout(batchTimeout);
    batchTimeout = null;
  }

  sendLogs(logsToSend);
}

/**
 * Create base log data
 */
function createBaseLog(level, eventType, action, message) {
  return {
    session_id: getSessionId(),
    log_level: level,
    event_type: eventType,
    action,
    message,
    url: window.location.href,
    user_agent: navigator.userAgent,
    created_at: new Date().toISOString()
  };
}

/**
 * Log an informational event
 * @param {string} action - Action identifier (e.g., 'create_assessment')
 * @param {string} message - Human-readable message
 * @param {object} details - Additional context
 */
export function logInfo(action, message, details = null) {
  const log = createBaseLog('info', 'user_action', action, message);
  if (details) log.details = details;
  queueLog(log);
}

/**
 * Log a warning
 * @param {string} action - Action identifier
 * @param {string} message - Warning message
 * @param {object} details - Additional context
 */
export function logWarn(action, message, details = null) {
  const log = createBaseLog('warn', 'warning', action, message);
  if (details) log.details = details;
  queueLog(log);
}

/**
 * Log an error
 * @param {string} action - Action where error occurred
 * @param {Error|string} error - Error object or message
 * @param {object} details - Additional context
 */
export function logError(action, error, details = null) {
  const errorMessage = error instanceof Error ? error.message : String(error);
  const errorStack = error instanceof Error ? error.stack : null;

  const log = createBaseLog('error', 'error', action, `Error: ${errorMessage}`);
  log.error_message = errorMessage;
  if (errorStack) log.error_stack = errorStack;
  if (details) log.details = details;

  queueLog(log);
}

/**
 * Log a page view
 * @param {string} pageName - Name of the page
 */
export function logPageView(pageName) {
  const log = createBaseLog('info', 'page_view', 'navigate', `Viewed ${pageName}`);
  queueLog(log);
}

/**
 * Log an API call
 * @param {string} endpoint - API endpoint
 * @param {string} method - HTTP method
 * @param {boolean} success - Whether call succeeded
 * @param {object} details - Request/response details
 */
export function logAPICall(endpoint, method, success, details = null) {
  const log = createBaseLog(
    success ? 'info' : 'error',
    'api_call',
    `api_${method.toLowerCase()}_${success ? 'success' : 'error'}`,
    `${method} ${endpoint} - ${success ? 'Success' : 'Failed'}`
  );
  if (details) log.details = details;
  queueLog(log);
}

/**
 * Log a console error
 * @param {string} message - Error message
 * @param {string} stack - Stack trace
 */
export function logConsoleError(message, stack = null) {
  const log = createBaseLog('error', 'console_error', 'console_error', message);
  if (stack) log.error_stack = stack;
  queueLog(log);
}

/**
 * Intercept console.error to auto-log errors
 */
export function interceptConsoleErrors() {
  const originalConsoleError = console.error;

  console.error = function (...args) {
    // Call original console.error
    originalConsoleError.apply(console, args);

    // Log to database
    const message = args.map(arg =>
      typeof arg === 'object' ? JSON.stringify(arg) : String(arg)
    ).join(' ');

    logConsoleError(message);
  };
}

/**
 * Listen for unhandled promise rejections
 */
export function interceptUnhandledRejections() {
  window.addEventListener('unhandledrejection', (event) => {
    logError(
      'unhandled_rejection',
      event.reason || 'Unhandled promise rejection',
      { reason: String(event.reason) }
    );
  });
}

/**
 * Initialize logging system
 */
export function initLogger() {
  // Initialize session ID
  getSessionId();

  // Intercept console errors
  interceptConsoleErrors();

  // Listen for unhandled rejections
  interceptUnhandledRejections();

  // Flush logs when page is about to unload
  window.addEventListener('beforeunload', () => {
    flushLogs();
  });

  // Flush logs periodically (every 30 seconds) to ensure delivery
  setInterval(flushLogs, 30000);

  console.log('[Logger] Application logging initialized');
}

// Auto-initialize when module loads
if (typeof window !== 'undefined') {
  // Small delay to ensure everything is loaded
  setTimeout(initLogger, 100);
}
